import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, Observable, ReplaySubject, of } from 'rxjs';
import { shareReplay, take, tap } from 'rxjs/operators';
import { BulkImportStepperService } from '../bulk-import-stepper.service';
import { CSVHeaderMappingInterface, FieldType } from '@vendasta/crm';
import { FieldSchemaInterface, ObjectType, CrmFormService, SystemFieldIds, StandardIds } from '@galaxy/crm/static';

const CUSTOM_OBJECT_PREFIX = 'CustomObject::';
const CUSTOM_OBJECT_DELIMITER = '::';

export interface FieldSchemaGroup {
  label: string;
  schemaOptions: FieldSchemaInterface[];
}

interface CrmObjectType {
  objectType: ObjectType;
  objectSubtype?: string;
}

@Injectable()
export class FieldMappingService {
  fieldMappings$: Observable<CSVHeaderMappingInterface[]>;
  importTypeValues$$ = new ReplaySubject<{ [key: number]: ObjectType }>(1);

  constructor(
    private readonly translate: TranslateService,
    private readonly formService: CrmFormService,
    private readonly stepperService: BulkImportStepperService,
  ) {
    this.fieldMappings$ = this.stepperService.csvMappings$;
    this.importTypeValues$$.next({});
  }

  private getObjectTypes(objectType: ObjectType): CrmObjectType {
    if (typeof objectType === 'string' && objectType.startsWith(CUSTOM_OBJECT_PREFIX)) {
      const subtype = objectType.split(CUSTOM_OBJECT_DELIMITER)[1];
      return {
        objectType: 'CustomObject' as ObjectType,
        objectSubtype: subtype,
      };
    }
    return { objectType };
  }

  private createFieldSchemaMap(schemas: FieldSchemaInterface[]): Map<string, FieldSchemaInterface> {
    const schemaMap = new Map<string, FieldSchemaInterface>();
    for (const schema of schemas) {
      schemaMap.set(schema.fieldId, schema);
    }
    return schemaMap;
  }

  getFieldSchemaMap$(objectType: ObjectType, objectSubtype?: string): Observable<Map<string, FieldSchemaInterface>> {
    const { objectType: objType, objectSubtype: objSubtype } = this.getObjectTypes(objectType);
    const subtype = objectSubtype || objSubtype;

    return this.formService
      .listAllFormFields$(objType, subtype)
      .pipe(map((schemas) => this.createFieldSchemaMap(schemas)));
  }

  displayField(s: FieldSchemaInterface | undefined): boolean {
    if (
      s?.fieldId == SystemFieldIds.CompanyID ||
      s?.fieldId == SystemFieldIds.ContactID ||
      s?.fieldId == SystemFieldIds.CustomObjectID ||
      s?.fieldId == SystemFieldIds.OpportunityID
    ) {
      return true;
    }
    //allow opportunity status / stage id fields to be displayed
    if (s?.fieldId == StandardIds.OpportunityStatus || s?.fieldId == StandardIds.OpportunityCalculatedStageID) {
      return true;
    }
    return !!s && !s.readOnly;
  }

  listFieldGroups$(objectType: ObjectType, objectSubtype?: string): Observable<FieldSchemaGroup[]> {
    const { objectType: objType, objectSubtype: objSubtype } = this.getObjectTypes(objectType);
    const subtype = objectSubtype || objSubtype;

    const fieldSchemaMap$ = this.getFieldSchemaMap$(objType, subtype);
    const fieldGroups$ = this.formService.listAllObjectFormFieldGroups$(objType, subtype ? of(subtype) : undefined);

    return combineLatest([fieldSchemaMap$, fieldGroups$]).pipe(
      map(([schemaMap, groups]) => {
        return groups.map((g) => ({
          label: this.translate.instant(g.description || 'BULK_IMPORT.MAP_FIELDS_STEP.OTHER_FIELDS'),
          schemaOptions: g.fieldIds
            .map((id) => schemaMap.get(id))
            .filter((field) => this.displayField(field)) as FieldSchemaInterface[],
        }));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private fieldAllowsMultipleMappings(fieldType: FieldType): boolean {
    const typesWithMultipleMappings = [FieldType.FIELD_TYPE_TAG];
    return typesWithMultipleMappings.includes(fieldType || FieldType.FIELD_TYPE_INVALID);
  }

  availableFieldGroups$(objectType: ObjectType): Observable<FieldSchemaGroup[]> {
    const { objectType: objType, objectSubtype } = this.getObjectTypes(objectType);

    return combineLatest([this.listFieldGroups$(objType, objectSubtype), this.fieldMappings$]).pipe(
      map(([groups, mappings]) => {
        const mappedFields = this.getMappedFields(objectType, mappings);

        return groups
          .map((g) => ({
            label: g.label,
            schemaOptions: g.schemaOptions.filter((option) => {
              return !mappedFields.includes(option.fieldId) || this.fieldAllowsMultipleMappings(option.fieldType);
            }),
          }))
          .filter((g) => g.schemaOptions.length > 0);
      }),
    );
  }

  private getMappedFields(objectType: ObjectType, mappings: CSVHeaderMappingInterface[]): string[] {
    const { objectType: objType, objectSubtype } = this.getObjectTypes(objectType);

    return mappings
      .filter((mapping) => {
        if (objType === 'CustomObject' && objectSubtype) {
          return mapping.crmObjectType === 'CustomObject' && mapping.crmObjectSubtype === objectSubtype;
        }
        return mapping.crmObjectType === objType;
      })
      .map((mapping) => mapping.fieldId)
      .filter((fieldId): fieldId is string => fieldId !== undefined);
  }

  addMapping(header: string, fieldId: string, objectType: string): void {
    this.stepperService.csvMappings$
      .pipe(
        take(1),
        map((mappings) => {
          const newMappings = Object.assign([], mappings); // If we don't do this, the stepper service won't detect the change

          const { objectType: parsedObjectType, objectSubtype } = this.getObjectTypes(objectType as ObjectType);

          newMappings.push({
            headerName: header,
            fieldId: fieldId,
            crmObjectType: parsedObjectType,
            crmObjectSubtype: objectSubtype,
          } as CSVHeaderMappingInterface);
          return newMappings;
        }),
        tap((mappings) => this.stepperService.setCSVHeaderMappings(mappings)),
      )
      .subscribe();
  }

  removeMapping(header: string): void {
    this.stepperService.csvMappings$
      .pipe(
        map((mappings) => {
          return mappings.filter((mapping) => mapping.headerName !== header);
        }),
        tap((mappings) => this.stepperService.setCSVHeaderMappings(mappings)),
        take(1),
      )
      .subscribe();
  }

  getImportTypeValues() {
    return this.importTypeValues$$;
  }

  resetImportTypeValues() {
    this.importTypeValues$$.next({});
  }
}
